import { Cart } from 'shared/types/cart/Cart';
import { Order } from 'shared/types/cart/Order';
import { UseCartReturn } from 'frontastic/hooks/useCart/types';

type MapCosts = (props: {
  reference?: 'cart' | 'order';
  cart?: Cart;
  order?: Order;
  currency: string;
}) => UseCartReturn['transaction'];

const mapCosts: MapCosts = ({ reference = 'order', cart, order, currency }) => {
  const data = { ...(reference === 'cart' ? cart : order) } as Cart;

  if (!data?.lineItems?.length) {
    return {
      subtotal: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      discount: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      tax: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      shipping: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      productDiscount: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      total: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
    };
  }

  const currencyCode = data.sum?.currencyCode ?? currency;
  const fractionDigits = data.sum?.fractionDigits ?? 2;

  const totalAmount = data.sum?.centAmount as number;
  const subTotalAmount = data.cartTotal?.centAmount as number;
  const discountOnTotal = data?.discountOnTotal?.discountedAmount?.centAmount || 0;
  const shippingDiscount = data?.shippingInfo?.shippingDiscounts?.reduce(
    (acc: number, curr: { discountedAmount: { centAmount: number } }) => acc + (curr.discountedAmount?.centAmount || 0),
    0,
  );

  const discountedAmount = data.lineItems.reduce(
    (acc, curr) => acc + ((curr.price?.centAmount || 0) * (curr.count as number) - (curr.totalPrice?.centAmount || 0)),
    0,
  );

  const cartTotalWithoutTax = data.taxed?.amount.centAmount ?? 0;
  const cartTaxAmount = totalAmount - cartTotalWithoutTax; // Inclusive of all taxes, including shipping tax
  let totalTax = cartTaxAmount; // All taxes including shippingTax
  if (data.shippingInfo?.price?.centAmount && data.shippingInfo.taxed?.centAmount) {
    // Show total tax on UI without the shippingPrice tax
    totalTax -= data.shippingInfo.taxed?.centAmount;
  }
  const discounts = data?.lineItems?.flatMap(
    (item) => item.discounts?.map((discount) => discount.discountedAmount?.centAmount || 0) || [0],
  ) || [0];
  let TotalDiscount = 0;
  if (discounts?.length > 0) {
    const cummulativeDiscount =
      ((discounts as number[])?.reduce((sum, amount) => sum + (amount || 0), 0) as number) || 0;
    TotalDiscount = -Math.abs(cummulativeDiscount);
  }

  const totalShipping =
    totalAmount > 0
      ? data.shippingInfo?.discountedPrice
        ? data.shippingInfo?.discountedPrice?.centAmount ?? 0
        : data.shippingInfo?.price?.centAmount ?? 0
      : 0;

  return {
    subtotal: {
      centAmount: subTotalAmount, // subTotal does not include tax
      currencyCode,
      fractionDigits,
    },
    discount: {
      centAmount: discountOnTotal,
      currencyCode,
      fractionDigits,
    },
    // shippingDiscount: {
    //   centAmount: shippingDiscount,
    //   currencyCode,
    //   fractionDigits,
    // },
    shipping: data?.shippingInfo
      ? {
          centAmount: totalShipping,
          currencyCode,
          fractionDigits,
        }
      : undefined,
    productDiscount:
      Math.abs(TotalDiscount) > 0
        ? {
            centAmount: TotalDiscount,
            currencyCode,
            fractionDigits,
          }
        : undefined,
    total: {
      centAmount: totalAmount, // totalAmount is inclusive of taxes
      currencyCode,
      fractionDigits,
    },
  };
};

export default mapCosts;
